import { cloneDeep, get, set } from 'lodash';
import {
  type ChangeEvent,
  type HTMLInputTypeAttribute,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { isDefined } from 'common';

import { useSelectedNodeSchemaContext } from './SelectedNodeSchemaContext';
import { useReactFlowViewContext } from '../../context/context';

type OnChangeEvent<TFieldValue> = Pick<
  ChangeEvent<{ value?: string | number | boolean | TFieldValue }>,
  'target'
>;
export interface UseSelectedNodeFieldProps<TFieldValue> {
  name: string;
  value: TFieldValue | undefined;
  onChange: (event: OnChangeEvent<TFieldValue>) => void;
  onBlur: () => void;
}
interface SelectedNodeFieldOptions {
  type?: HTMLInputTypeAttribute;
  includeChildErrors?: boolean;
}

export function useSelectedNodeField<TFieldValue>(
  fieldName: string,
  options: SelectedNodeFieldOptions = {}
) {
  const { type, includeChildErrors } = options;
  const { selectedNodeData, selectedNodeId, reactFlowMutators } = useReactFlowViewContext();
  const { selectedNodeErrors } = useSelectedNodeSchemaContext();

  const { updateNodeData } = reactFlowMutators ?? {};
  /**
   * TODO - we are making these always touched. We will likely refactor this to be based on "new" block vs
   * opening a previously created block.
   */
  const [fieldIsTouched, setFieldIsTouched] = useState(true);
  const error = useMemo(() => {
    return selectedNodeErrors?.find((issue) => {
      const issuePath = issue.path.join('.');
      return (
        issuePath === fieldName || (includeChildErrors && issuePath.startsWith(fieldName + '.'))
      );
    })?.message;
  }, [fieldName, includeChildErrors, selectedNodeErrors]);
  const isFieldErrorVisible = isDefined(error) && fieldIsTouched;
  const visibleError = isFieldErrorVisible ? error : undefined;

  const value = useMemo(() => {
    return isDefined(selectedNodeData)
      ? (get(selectedNodeData, fieldName) as TFieldValue)
      : undefined;
  }, [fieldName, selectedNodeData]);

  const setValue = useCallback(
    (value: TFieldValue) => {
      if (isDefined(selectedNodeId)) {
        updateNodeData?.(selectedNodeId, (node) => {
          /**
           * We must clone the data object to ensure we don't mutate the original data object.
           * If you remove this, then you will see things like change detection not working.
           * (The save button will stay disabled even after you make a change).
           *
           * structuredClone is a built-in JavaScript function that creates a deep copy of a value.
           * It is faster and more efficient than using lodash's cloneDeep.
           */
          const clonedData =
            typeof structuredClone === 'function'
              ? structuredClone(node.data)
              : cloneDeep(node.data);
          return set(clonedData, fieldName, value);
        });
      }
    },
    [fieldName, updateNodeData, selectedNodeId]
  );

  const onChange = useCallback(
    (event: OnChangeEvent<TFieldValue>) => {
      let { value } = event.target;
      if (type === 'number') {
        value = isDefined(value) && value !== '' ? Number(value) : value;
      }
      if (type === 'boolean') {
        value = isDefined(value) && value !== '' ? value === 'true' : value;
      }
      setValue(value as TFieldValue);
    },
    [setValue, type]
  );

  const onBlur = useCallback(() => {
    setFieldIsTouched(true);
  }, []);

  const field: UseSelectedNodeFieldProps<TFieldValue> = useMemo(() => {
    return {
      name: fieldName,
      value,
      onChange,
      onBlur,
    };
  }, [fieldName, value, onChange, onBlur]);

  return {
    field,
    value,
    setValue,
    isFieldErrorVisible,
    visibleError,
  };
}
